import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';

const MAP_TEMPO = {
  ate_30_dias: 'Até 30 dias', ate_6_meses: 'Até 6 meses', ate_1_ano: 'Até 1 ano',
  ate_5_anos: 'Até 5 anos', ate_10_anos: 'Até 10 anos', mais_de_10_anos: 'Mais de 10 anos',
};
const MAP_QUEIXAS = {
  dor_axial: 'Dor axial', radiculopatia: 'Radiculopatia', perda_forca: 'Perda de força',
  perda_sensibilidade: 'Perda de sensibilidade', claudicacao: 'Claudicação',
  perda_controle_esfincteriano: 'Perda de controle esfincteriano',
};
const MAP_PELE = {
  integra: 'Íntegra', cicatriz_mielomeningocele: 'Cicatriz — Mielomeningocele',
  cicatriz_abordagem_previa: 'Cicatriz — Abordagem prévia', exposicao: 'Exposição', fistula: 'Fístula',
};
const MAP_NEURO = {
  preservado: 'Preservado', acometimento_radicular_mmss: 'Acometimento Radicular MMSS',
  acometimento_medular_mmss: 'Acometimento Medular MMSS', acometimento_radicular_mmii: 'Acometimento Radicular MMII',
  acometimento_medular_mmii: 'Acometimento Medular MMII',
};
const MAP_URINA = {
  espontaneo: 'Espontâneo', fralda: 'Fralda', sonda_alivio: 'Sonda de Alívio', cateter_demora: 'Cateter de Demora',
};
const MAP_CIR = {
  nao: 'Não', discectomia_mesmo_sitio: 'Discectomia no mesmo sítio',
  artrodese_mesmo_sitio: 'Artrodese no mesmo sítio', discectomia_outro_sitio: 'Discectomia em outro sítio',
  artrodese_outro_sitio: 'Artrodese em outro sítio',
};
const DIAGNOSTICOS_OPCOES = [
  'Estenose', 'Compressão radicular', 'Mielopatia', 'Tumor ósseo', 'Tumor raquidiano',
  'Espondilolistese', 'Escoliose idiopática', 'Escoliose neuromuscular', 'Escoliose sindrômica',
  'Invaginação basilar', 'Fratura', 'Espondilodiscite', 'Tuberculose óssea', 'Doença de Scheuermann',
];

function fmtData(d) {
  if (!d) return '—';
  try { return format(new Date(d.includes('T') ? d : d + 'T12:00:00'), 'dd/MM/yyyy'); }
  catch { return d; }
}

function tradArr(map, arr) {
  if (!arr || !arr.length) return '—';
  return arr.map(v => map[v] || v).join(', ');
}

function avToForm(av) {
  return {
    tempo_sintomas: av.tempo_sintomas || '',
    queixas: av.queixas || [],
    pele: av.pele || '',
    neurologico: av.neurologico || [],
    nivel_medular: av.nivel_medular || '',
    raizes_afetadas: av.raizes_afetadas || '',
    urina: av.urina || '',
    cirurgia_previa: av.cirurgia_previa || [],
    ano_cirurgia_previa: av.ano_cirurgia_previa != null ? String(av.ano_cirurgia_previa) : '',
    infeccao_previa: av.infeccao_previa === true ? 'sim' : av.infeccao_previa === false ? 'nao' : '',
    diagnosticos: av.diagnosticos || [],
    diagnostico_outro: av.diagnostico_outro || '',
  };
}

const FORM_EMPTY = {
  tempo_sintomas: '', queixas: [], pele: '', neurologico: [],
  nivel_medular: '', raizes_afetadas: '', urina: '',
  cirurgia_previa: [], ano_cirurgia_previa: '', infeccao_previa: '',
  diagnosticos: [], diagnostico_outro: '',
};

function ChkLabel({ checked, disabled, label, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: disabled ? 'default' : 'pointer', padding: '7px 12px', border: `1px solid ${checked ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, background: checked ? '#eff6ff' : 'var(--surface)', opacity: disabled ? .45 : 1, fontSize: 14, transition: 'all .15s', userSelect: 'none' }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} style={{ accentColor: 'var(--blue)' }} />
      {label}
    </label>
  );
}

function RadioLabel({ checked, name, value, label, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '7px 12px', border: `1px solid ${checked ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, background: checked ? '#eff6ff' : 'var(--surface)', fontSize: 14, transition: 'all .15s', userSelect: 'none' }}>
      <input type="radio" name={name} value={value} checked={checked} onChange={onChange} style={{ accentColor: 'var(--blue)' }} />
      {label}
    </label>
  );
}

function FieldView({ label, value }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="form-label" style={{ marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14 }}>{value || '—'}</div>
    </div>
  );
}

// ─── Formulário ───────────────────────────────────────────────────────────────
function FormPreOp({ pacienteId, avaliacao, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(avaliacao ? avToForm(avaliacao) : { ...FORM_EMPTY });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function handleDiagnosticoToggle(valor) {
    setForm(prev => {
      const atual = prev.diagnosticos || [];
      const novo = atual.includes(valor) ? atual.filter(d => d !== valor) : [...atual, valor];
      return { ...prev, diagnosticos: novo };
    });
  }

  async function handleSave(assinar) {
    if (!form.tempo_sintomas) { setError('Campo "Tempo de sintomas" é obrigatório.'); return; }
    setSaving(true);
    setError('');
    const payload = {
      tempo_sintomas: form.tempo_sintomas,
      queixas: form.queixas,
      pele: form.pele || null,
      neurologico: form.neurologico,
      nivel_medular: form.nivel_medular || null,
      raizes_afetadas: form.raizes_afetadas || null,
      urina: form.urina || null,
      cirurgia_previa: form.cirurgia_previa,
      ano_cirurgia_previa: form.ano_cirurgia_previa ? Number(form.ano_cirurgia_previa) : null,
      infeccao_previa: form.infeccao_previa === 'sim' ? true : form.infeccao_previa === 'nao' ? false : null,
      diagnosticos: form.diagnosticos || [],
      diagnostico_outro: form.diagnostico_outro || null,
      assinada: assinar,
      ...(assinar ? { assinada_por: user.id, assinada_em: new Date().toISOString() } : {}),
    };
    let err;
    if (avaliacao) {
      ({ error: err } = await supabase.from('avaliacoes_preop').update(payload).eq('id', avaliacao.id));
    } else {
      ({ error: err } = await supabase.from('avaliacoes_preop').insert({ ...payload, paciente_id: pacienteId }));
    }
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved();
  }

  const assinada = avaliacao?.assinada || false;
  const cirHab = form.cirurgia_previa.length > 0 && !form.cirurgia_previa.includes('nao');
  const secStyle = { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 };
  const secTitle = { fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 14 };

  return (
    <div>
      {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>⚠ {error}</div>}

      {/* Anamnese */}
      <div style={secStyle}>
        <div style={secTitle}>Anamnese</div>
        <div className="form-group">
          <label className="form-label">Tempo de sintomas *</label>
          <select className="form-select" value={form.tempo_sintomas} onChange={e => set('tempo_sintomas', e.target.value)}>
            <option value="">Selecione...</option>
            {Object.entries(MAP_TEMPO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Queixas</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(MAP_QUEIXAS).map(([k, label]) => (
              <ChkLabel key={k} label={label} checked={form.queixas.includes(k)} disabled={false}
                onChange={() => setForm(f => ({ ...f, queixas: f.queixas.includes(k) ? f.queixas.filter(x => x !== k) : [...f.queixas, k] }))} />
            ))}
          </div>
        </div>
      </div>

      {/* Exame Físico */}
      <div style={secStyle}>
        <div style={secTitle}>Exame Físico</div>
        <div className="form-group">
          <label className="form-label">Pele</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(MAP_PELE).map(([k, label]) => (
              <ChkLabel key={k} label={label} checked={form.pele === k}
                disabled={form.pele === 'integra' && k !== 'integra'}
                onChange={() => set('pele', form.pele === k ? '' : k)} />
            ))}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Neurológico</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(MAP_NEURO).map(([k, label]) => (
              <ChkLabel key={k} label={label} checked={form.neurologico.includes(k)}
                disabled={form.neurologico.includes('preservado') && k !== 'preservado'}
                onChange={() => {
                  if (k === 'preservado') {
                    setForm(f => ({ ...f, neurologico: f.neurologico.includes('preservado') ? [] : ['preservado'] }));
                  } else {
                    setForm(f => ({ ...f, neurologico: f.neurologico.includes(k) ? f.neurologico.filter(x => x !== k) : [...f.neurologico, k] }));
                  }
                }} />
            ))}
          </div>
        </div>
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Nível medular afetado</label>
            <input className="form-input" value={form.nivel_medular} onChange={e => set('nivel_medular', e.target.value)} placeholder="Ex: C5, L4..." />
          </div>
          <div className="form-group">
            <label className="form-label">Raízes afetadas</label>
            <input className="form-input" value={form.raizes_afetadas} onChange={e => set('raizes_afetadas', e.target.value)} placeholder="Ex: L4, L5, S1..." />
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Urina</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(MAP_URINA).map(([k, label]) => (
              <RadioLabel key={k} name="urina_preop" value={k} label={label} checked={form.urina === k} onChange={() => set('urina', k)} />
            ))}
          </div>
        </div>
      </div>

      {/* HPP */}
      <div style={secStyle}>
        <div style={secTitle}>HPP — História Patológica Pregressa</div>
        <div className="form-group">
          <label className="form-label">Cirurgia prévia</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(MAP_CIR).map(([k, label]) => (
              <ChkLabel key={k} label={label} checked={form.cirurgia_previa.includes(k)}
                disabled={form.cirurgia_previa.includes('nao') && k !== 'nao'}
                onChange={() => {
                  if (k === 'nao') {
                    setForm(f => ({ ...f, cirurgia_previa: f.cirurgia_previa.includes('nao') ? [] : ['nao'] }));
                  } else {
                    setForm(f => ({ ...f, cirurgia_previa: f.cirurgia_previa.includes(k) ? f.cirurgia_previa.filter(x => x !== k) : [...f.cirurgia_previa, k] }));
                  }
                }} />
            ))}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label" style={{ opacity: cirHab ? 1 : .6 }}>Ano da cirurgia prévia (aproximado)</label>
          <input className="form-input" type="number" min={1900} max={2100}
            value={form.ano_cirurgia_previa} onChange={e => set('ano_cirurgia_previa', e.target.value)}
            disabled={!cirHab} placeholder="Ex: 2015" style={{ opacity: cirHab ? 1 : .45 }} />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Infecção prévia</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['sim', 'Sim'], ['nao', 'Não']].map(([k, label]) => (
              <RadioLabel key={k} name="infeccao_previa_preop" value={k} label={label} checked={form.infeccao_previa === k} onChange={() => set('infeccao_previa', k)} />
            ))}
          </div>
        </div>
      </div>

      {/* Diagnósticos */}
      <div style={secStyle}>
        <div style={secTitle}>Diagnósticos</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
          {DIAGNOSTICOS_OPCOES.map(opcao => (
            <ChkLabel key={opcao} label={opcao}
              checked={form.diagnosticos?.includes(opcao)}
              disabled={assinada}
              onChange={() => handleDiagnosticoToggle(opcao)} />
          ))}
          <ChkLabel label="Outro"
            checked={form.diagnosticos?.includes('Outro')}
            disabled={assinada}
            onChange={() => handleDiagnosticoToggle('Outro')} />
        </div>
        {form.diagnosticos?.includes('Outro') && (
          <input className="form-input" style={{ marginTop: 10 }}
            value={form.diagnostico_outro}
            onChange={e => set('diagnostico_outro', e.target.value)}
            disabled={assinada}
            placeholder="Descreva o diagnóstico..." />
        )}
      </div>

      <div className="flex-row" style={{ justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
        <button className="btn btn-secondary" onClick={() => handleSave(false)} disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar Rascunho'}
        </button>
        <button className="btn btn-primary" onClick={() => handleSave(true)} disabled={saving}>
          {saving ? 'Assinando...' : '✓ Assinar e Finalizar'}
        </button>
      </div>
    </div>
  );
}

// ─── Modal de visualização ─────────────────────────────────────────────────────
function ModalVerPreOp({ avaliacao, canEdit, pacienteId, onClose, onSaved }) {
  const [assinante, setAssinante] = useState('');
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (!avaliacao.assinada_por) return;
    supabase.from('profiles').select('nome_completo').eq('id', avaliacao.assinada_por).maybeSingle()
      .then(({ data }) => setAssinante(data?.nome_completo || ''));
  }, [avaliacao.assinada_por]);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <h3>{editMode ? 'Editar Avaliação Pré-Op' : 'Avaliação Pré-Op'}</h3>
          <button className="btn-close" onClick={editMode ? () => setEditMode(false) : onClose}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {editMode ? (
            <FormPreOp
              pacienteId={pacienteId}
              avaliacao={avaliacao}
              onClose={() => setEditMode(false)}
              onSaved={onSaved}
            />
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16, alignItems: 'center' }}>
                <span className={`badge ${avaliacao.assinada ? 'badge-teal' : 'badge-orange'}`}>
                  {avaliacao.assinada ? 'Assinada ✅' : 'Em preenchimento 🟡'}
                </span>
                <span className="text-sm text-muted">Criada em {fmtData(avaliacao.criada_em)}</span>
                {avaliacao.assinada && assinante && (
                  <span className="text-sm text-muted">
                    · Assinada por {assinante}{avaliacao.assinada_em ? ` em ${fmtData(avaliacao.assinada_em)}` : ''}
                  </span>
                )}
              </div>

              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 12 }}>Anamnese</div>
                <FieldView label="Tempo de sintomas" value={MAP_TEMPO[avaliacao.tempo_sintomas]} />
                <FieldView label="Queixas" value={tradArr(MAP_QUEIXAS, avaliacao.queixas)} />
              </div>

              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 12 }}>Exame Físico</div>
                <FieldView label="Pele" value={MAP_PELE[avaliacao.pele]} />
                <FieldView label="Neurológico" value={tradArr(MAP_NEURO, avaliacao.neurologico)} />
                <FieldView label="Nível medular afetado" value={avaliacao.nivel_medular} />
                <FieldView label="Raízes afetadas" value={avaliacao.raizes_afetadas} />
                <FieldView label="Urina" value={MAP_URINA[avaliacao.urina]} />
              </div>

              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 12 }}>HPP — História Patológica Pregressa</div>
                <FieldView label="Cirurgia prévia" value={tradArr(MAP_CIR, avaliacao.cirurgia_previa)} />
                {avaliacao.ano_cirurgia_previa != null && (
                  <FieldView label="Ano da cirurgia prévia" value={String(avaliacao.ano_cirurgia_previa)} />
                )}
                <FieldView label="Infecção prévia" value={avaliacao.infeccao_previa === true ? 'Sim' : avaliacao.infeccao_previa === false ? 'Não' : undefined} />
              </div>

              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 12 }}>Diagnósticos</div>
                <FieldView label="Diagnósticos"
                  value={avaliacao.diagnosticos?.length ? avaliacao.diagnosticos.join(', ') : undefined} />
                {avaliacao.diagnostico_outro && (
                  <FieldView label="Outro" value={avaliacao.diagnostico_outro} />
                )}
              </div>
            </>
          )}
        </div>
        {!editMode && (
          <div className="modal-footer">
            {!avaliacao.assinada && canEdit && (
              <button className="btn btn-secondary" onClick={() => setEditMode(true)}>Editar</button>
            )}
            <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function AvaliacaoPreOpTab({ pacienteId, canEdit }) {
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [verAvaliacao, setVerAvaliacao] = useState(null);

  async function loadAvaliacoes() {
    setLoading(true);
    const { data } = await supabase.from('avaliacoes_preop').select('*')
      .eq('paciente_id', pacienteId)
      .order('criada_em', { ascending: false });
    setAvaliacoes(data || []);
    setLoading(false);
  }

  useEffect(() => { loadAvaliacoes(); }, [pacienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSaved() {
    setShowForm(false);
    setVerAvaliacao(null);
    loadAvaliacoes();
  }

  if (loading) {
    return <div className="text-muted text-sm" style={{ padding: 24, textAlign: 'center' }}>Carregando...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {avaliacoes.length === 0 && !showForm ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>Nenhuma avaliação pré-operatória registrada</h3>
            {canEdit && (
              <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => setShowForm(true)}>
                + Nova Avaliação Pré-Op
              </button>
            )}
          </div>
        </div>
      ) : (
        avaliacoes.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h2>Avaliações Pré-Operatórias</h2>
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {avaliacoes.map(av => (
                  <div key={av.id}
                    onClick={() => setVerAvaliacao(av)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer', background: 'var(--surface)', transition: 'background .15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>Avaliação de {fmtData(av.criada_em)}</div>
                      {av.tempo_sintomas && (
                        <div className="text-sm text-muted">{MAP_TEMPO[av.tempo_sintomas]}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span className={`badge ${av.assinada ? 'badge-teal' : 'badge-orange'}`}>
                        {av.assinada ? 'Assinada ✅' : 'Em preenchimento 🟡'}
                      </span>
                      <span className="btn btn-sm btn-secondary">Ver →</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      )}

      {avaliacoes.length > 0 && canEdit && !showForm && (
        <button className="btn btn-secondary" onClick={() => setShowForm(true)}>+ Nova Avaliação Pré-Op</button>
      )}

      {showForm && (
        <div className="card">
          <div className="card-header">
            <h2>Nova Avaliação Pré-Op</h2>
            <button className="btn-close" onClick={() => setShowForm(false)}>×</button>
          </div>
          <div className="card-body">
            <FormPreOp
              pacienteId={pacienteId}
              avaliacao={null}
              onClose={() => setShowForm(false)}
              onSaved={handleSaved}
            />
          </div>
        </div>
      )}

      {verAvaliacao && (
        <ModalVerPreOp
          avaliacao={verAvaliacao}
          canEdit={canEdit}
          pacienteId={pacienteId}
          onClose={() => setVerAvaliacao(null)}
          onSaved={() => { setVerAvaliacao(null); loadAvaliacoes(); }}
        />
      )}
    </div>
  );
}
